import { lazy, Suspense } from "react";
import { Box, Button } from "@mui/material";
import SupportAgentRoundedIcon from "@mui/icons-material/SupportAgentRounded";
import ScrollToTop from "./components/scrollToTop/scrollToTop";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useLocation,
} from "react-router-dom";
import { usePopup } from "./contexts/popupContext";
import Popup from "./components/popup/popup";
import Nav from "./components/nav/nav";
import IssuerZoneNav from "./components/nav/IssuerZoneNav";
import { LocaleProvider } from "./components/aboutUs/LocaleContext";
import PhoneNav from "./components/phonenav/phonenav";
import SplashScreen from "./components/splashScreen/splashScreen";
import Footer from "./components/footer/footer";
import { loginGoto } from "./sections/loginPopups/loginPopups";
import MainContent from "./components/newsletter/MainContent";
import About from "./components/aboutUs/About";
import AuthDialog from "./sections/loginPopups/AuthDialog";
import InactivityTimer from "./components/InactivityTimer/InactivityTimer";
import ApprovedIdPurchaseGate from "./components/checkout/ApprovedIdPurchaseGate";
import SentryTestButton, { shouldRenderSentryTestButton } from "./components/sentry/SentryTestButton";
import TermsAndConditions from "./pages/termsAndCondition/TermsAndConditions";
import Checkout from "./sections/checkout/checkout";
import BridgeStripeDevPage from "./payments/bridgeStripe/BridgeStripeDevPage";
import BridgeStripeSandbox from "./payments/bridgeStripe/BridgeStripeSandbox";
const FilterPage = lazy(() => import("./pages/filterPage/filterPage"));
const ExplorePage = lazy(() => import("./pages/explorePage/explorePage"));
const ReadMore = lazy(() => import("./pages/readMore/readMore"));
const Portfolio = lazy(() => import("./pages/portfolio/portfolio"));
const PaymentPage = lazy(() => import("./pages/paymentPage/paymentPage"));
const CheckoutComplete = lazy(() => import("./pages/checkoutComplete.jsx"));
const CheckoutMethodPage = lazy(() => import("./pages/checkout/CheckoutMethodPage.jsx"));
const CryptoCheckoutPage = lazy(() => import("./pages/checkout/CryptoCheckoutPage.jsx"));
const PixCheckoutPage = lazy(() => import("./pages/checkout/PixCheckoutPage.jsx"));
const ManualPaymentCheckoutPage = lazy(() => import("./pages/checkout/ManualPaymentCheckoutPage.jsx"));
const BridgeCheckoutComplete = lazy(() => import("./pages/bridgeCheckoutComplete.jsx"));
const FAQPage = lazy(() => import("./pages/faq/FAQPage"));
const LoginRegisterPage = lazy(() => import("./pages/auth/LoginRegisterPage"));
const IssuerWorkspacePage = lazy(() => import("./pages/auth/IssuerWorkspacePage"));
const IssuerDashboardPage = lazy(() => import("./pages/auth/IssuerDashboardPage"));
const IssuerOnboardingPage = lazy(() => import("./pages/issuerOnboarding/IssuerOnboardingPage"));
const AdminPreviewPage = lazy(() => import("./pages/admin/AdminPreviewPage"));

function ZoneNavigation() {
  const location = useLocation();
  const inIssuerZone = location.pathname.startsWith('/issuer-') && location.pathname !== '/issuer-admin';

  if (inIssuerZone) {
    return <IssuerZoneNav />;
  }

  return <Nav />;
}

function App() {
  const popupContext = usePopup();

  return (
    <LocaleProvider initialLocale="pt-BR">
      <AuthDialog>
        <Router basename={"/"}>
          {popupContext.screens.length > 0 && (
            <Popup>
              {popupContext.screens.map((s) => (
                <Box
                  key={s.id}
                  width={"100%"}
                  height="100%"
                  position={"absolute"}
                  sx={{
                    top: '50%',
                    transform: `translate(${s.offset}, -50%)`,
                    transition: "0.5s",
                    overflowY: 'auto',
                    "&.transition-enter": {
                      transform: "translate(0%, -50%)",
                    },
                  }}
                >
                  {s.element}
                </Box>
              ))}
              <Button
                sx={{
                  position: "absolute",
                  borderRadius: "100%",
                  padding: 0,
                  minWidth: 0,
                  width: "34px",
                  height: "34px",
                  top: 12,
                  right: 12,
                  boxShadow: "0 8px 20px rgba(2, 6, 23, 0.2)",
                  border: '1px solid rgba(15, 23, 42, 0.08)',
                  backgroundColor: '#fff',
                  color: '#111827',
                  '&:hover': { backgroundColor: '#f8fafc' }
                }}
                onClick={popupContext.goBack}
              >
                &larr;
              </Button>
            </Popup>
          )}
          {/* Route-aware top navigation */}
          <ZoneNavigation />
          <ScrollToTop />
          <Routes>
            <Route
              exact
              path="/"
              element={
                <Suspense fallback={<SplashScreen />}>
                  <ExplorePage />
                </Suspense>
              }
            />
            <Route path="/index.html" element={<Navigate to="/" replace />} />
            <Route
              path="/bridge-stripe"
              element={
                <Suspense fallback={<SplashScreen />}>
                  <BridgeStripeDevPage />
                </Suspense>
              }

            />
            <Route
              path="/bridge-stripe-sandbox"
              element={
                <Suspense fallback={<SplashScreen />}>
                  <BridgeStripeSandbox />
                </Suspense>
              }

            />
            <Route
              path="/explore"
              element={
                <Suspense fallback={<SplashScreen />}>
                  <FilterPage />
                </Suspense>
              }
            />
            <Route
              path="/portfolio"
              element={
                <Suspense fallback={<SplashScreen />}>
                  <Portfolio />
                </Suspense>
              }
            />
            <Route
              path="/paymentPage"
              element={
                <Suspense fallback={<SplashScreen />}>
                  <PaymentPage />
                </Suspense>
              }
            />
            <Route
              path="/interest"
              element={
                <Suspense fallback={<SplashScreen />}>
                  <MainContent />
                </Suspense>
              }
            />
            <Route
              path="/about"
              element={
                <Suspense fallback={<SplashScreen />}>
                  <About />
                </Suspense>
              }
            />
            <Route
              path="/project"
              element={
                <Suspense fallback={<SplashScreen />}>
                  <ReadMore />
                </Suspense>
              }
            />
            <Route
              path="/readMore"
              element={
                <Suspense fallback={<SplashScreen />}>
                  <ReadMore />
                </Suspense>
              }
            />
            <Route
              path="/terms-and-conditions"
              element={
                <Suspense fallback={<SplashScreen />}>
                  <TermsAndConditions />
                </Suspense>
              }
            />
            <Route
              path="/checkout"
              element={
                <ApprovedIdPurchaseGate>
                  <Suspense fallback={<SplashScreen />}>
                    <Checkout />
                  </Suspense>
                </ApprovedIdPurchaseGate>
              }
            />
            <Route
              path="/checkout/method"
              element={
                <ApprovedIdPurchaseGate>
                  <Suspense fallback={<SplashScreen />}>
                    <CheckoutMethodPage />
                  </Suspense>
                </ApprovedIdPurchaseGate>
              }
            />
            <Route
              path="/checkout/card"
              element={
                <ApprovedIdPurchaseGate>
                  <Suspense fallback={<SplashScreen />}>
                    <Checkout />
                  </Suspense>
                </ApprovedIdPurchaseGate>
              }
            />
            <Route
              path="/checkout/crypto"
              element={
                <ApprovedIdPurchaseGate>
                  <Suspense fallback={<SplashScreen />}>
                    <CryptoCheckoutPage />
                  </Suspense>
                </ApprovedIdPurchaseGate>
              }
            />
            <Route
              path="/checkout/pix"
              element={
                <ApprovedIdPurchaseGate>
                  <Suspense fallback={<SplashScreen />}>
                    <PixCheckoutPage />
                  </Suspense>
                </ApprovedIdPurchaseGate>
              }
            />
            <Route
              path="/checkout/manual"
              element={
                <ApprovedIdPurchaseGate>
                  <Suspense fallback={<SplashScreen />}>
                    <ManualPaymentCheckoutPage />
                  </Suspense>
                </ApprovedIdPurchaseGate>
              }
            />
            <Route
              path="/faq"
              element={
                <Suspense fallback={<SplashScreen />}>
                  <FAQPage />
                </Suspense>
              }
            />
            <Route
              path="/bridgeCheckoutComplete"
              element={
                <Suspense fallback={<SplashScreen />}>
                  <BridgeCheckoutComplete />
                </Suspense>
              }
            />
            <Route
              path="/checkoutComplete"
              element={
                <Suspense fallback={<SplashScreen />}>
                  <CheckoutComplete />
                </Suspense>
              }
            />
            <Route
              path="/auth"
              element={
                <Suspense fallback={<SplashScreen />}>
                  <LoginRegisterPage />
                </Suspense>
              }
            />
            <Route
              path="/issuer-auth"
              element={
                <Suspense fallback={<SplashScreen />}>
                  <IssuerWorkspacePage />
                </Suspense>
              }
            />
            <Route
              path="/issuer-dashboard"
              element={
                <Suspense fallback={<SplashScreen />}>
                  <IssuerDashboardPage />
                </Suspense>
              }
            />
            <Route
              path="/become-an-issuer"
              element={
                <Suspense fallback={<SplashScreen />}>
                  <IssuerOnboardingPage />
                </Suspense>
              }
            />
            <Route
              path="/issuer-admin"
              element={
                <Suspense fallback={<SplashScreen />}>
                  <AdminPreviewPage role="issuer" />
                </Suspense>
              }
            />
            <Route
              path="/staff-admin"
              element={
                <Suspense fallback={<SplashScreen />}>
                  <AdminPreviewPage role="staff" />
                </Suspense>
              }
            />
          </Routes>
          <Footer />
          <Button
            component="a"
            href="https://innovacrypto.com.br/support"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Open support page"
            startIcon={<SupportAgentRoundedIcon sx={{ fontSize: 18 }} />}
            sx={{
              position: "fixed",
              right: { xs: 16, sm: 24 },
              bottom: { xs: 16, sm: 24 },
              zIndex: 1300,
              borderRadius: "999px",
              px: 2.2,
              py: 1,
              minWidth: 0,
              border: "1px solid rgba(255, 255, 255, 0.2)",
              boxShadow: "0 14px 30px rgba(15, 23, 42, 0.24)",
              background: "linear-gradient(135deg, #0f766e 0%, #0d9488 100%)",
              color: "#ffffff",
              fontWeight: 700,
              letterSpacing: 0.2,
              textTransform: "none",
              backdropFilter: "blur(6px)",
              transition: "transform 160ms ease, box-shadow 160ms ease",
              "&:hover": {
                boxShadow: "0 18px 36px rgba(15, 23, 42, 0.28)",
                transform: "translateY(-2px)",
                background: "linear-gradient(135deg, #115e59 0%, #0f766e 100%)",
              },
            }}
          >
            Support
          </Button>
          {shouldRenderSentryTestButton() ? <SentryTestButton /> : null}
        </Router>
        <InactivityTimer timeout={100000} />
      </AuthDialog>
    </LocaleProvider>
  );
}

export default App;
